import { createRouter, createWebHistory } from 'vue-router'
import { authGuard } from './guards'

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { requiresAuth: false, title: 'Login' },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/auth/ForgotPasswordView.vue'),
      meta: { requiresAuth: false, title: 'Forgot Password' },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/auth/ResetPasswordView.vue'),
      meta: { requiresAuth: false, title: 'Reset Password' },
    },
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/views/dashboard/DashboardIndex.vue'),
      meta: { requiresAuth: true, title: 'Dashboard', breadcrumb: 'Dashboard' },
    },
    // CRM Routes
    {
      path: '/crm/customers',
      name: 'crm-customers',
      component: () => import('@/views/crm/customers/CustomerList.vue'),
      meta: { requiresAuth: true, title: 'Customers', breadcrumb: 'CRM / Customers' },
    },
    // COMMENTED OUT - Using slide-in drawer forms now
    // {
    //   path: '/crm/customers/create',
    //   name: 'crm-customers-create',
    //   component: () => import('@/views/crm/customers/CustomerCreate.vue'),
    //   meta: { requiresAuth: true, title: 'Add Customer', breadcrumb: 'CRM / Customers / Add' }
    // },
    {
      path: '/crm/customers/:id',
      name: 'crm-customers-detail',
      component: () => import('@/views/crm/customers/CustomerDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Customer Details',
        breadcrumb: 'CRM / Customers / Details',
      },
    },
    // COMMENTED OUT - Using slide-in drawer forms now
    // {
    //   path: '/crm/customers/:id/edit',
    //   name: 'crm-customers-edit',
    //   component: () => import('@/views/crm/customers/CustomerCreate.vue'),
    //   meta: { requiresAuth: true, title: 'Edit Customer', breadcrumb: 'CRM / Customers / Edit' }
    // },
    {
      path: '/crm/contacts',
      name: 'crm-contacts',
      component: () => import('@/views/crm/contacts/ContactList.vue'),
      meta: { requiresAuth: true, title: 'Contacts', breadcrumb: 'CRM / Contacts' },
    },
    {
      path: '/crm/contacts/:id',
      name: 'crm-contacts-detail',
      component: () => import('@/views/crm/contacts/ContactDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Contact Details',
        breadcrumb: 'CRM / Contacts / Details',
      },
    },
    {
      path: '/crm/leads',
      name: 'crm-leads',
      component: () => import('@/views/crm/leads/LeadList.vue'),
      meta: { requiresAuth: true, title: 'Leads', breadcrumb: 'CRM / Leads' },
    },
    {
      path: '/crm/leads/kanban',
      name: 'crm-leads-kanban',
      component: () => import('@/views/crm/leads/LeadKanban.vue'),
      meta: { requiresAuth: true, title: 'Lead Pipeline', breadcrumb: 'CRM / Leads / Kanban' },
    },
    {
      path: '/crm/leads/:id',
      name: 'crm-leads-detail',
      component: () => import('@/views/crm/leads/LeadDetail.vue'),
      meta: { requiresAuth: true, title: 'Lead Details', breadcrumb: 'CRM / Leads / Details' },
    },
    {
      path: '/crm/opportunities',
      name: 'crm-opportunities',
      component: () => import('@/views/crm/opportunities/OpportunityList.vue'),
      meta: { requiresAuth: true, title: 'Opportunities', breadcrumb: 'CRM / Opportunities' },
    },
    {
      path: '/crm/opportunities/:id',
      name: 'crm-opportunities-detail',
      component: () => import('@/views/crm/opportunities/OpportunityDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Opportunity Details',
        breadcrumb: 'CRM / Opportunities / Details',
      },
    },
    {
      path: '/crm/quotations',
      name: 'crm-quotations',
      component: () => import('@/views/crm/quotations/QuotationList.vue'),
      meta: { requiresAuth: true, title: 'Quotations', breadcrumb: 'CRM / Quotations' },
    },
    {
      path: '/crm/quotations/:id',
      name: 'crm-quotations-detail',
      component: () => import('@/views/crm/quotations/QuotationDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Quotation Details',
        breadcrumb: 'CRM / Quotations / Details',
      },
    },
    {
      path: '/crm/pricing',
      name: 'crm-pricing',
      component: () => import('@/views/crm/pricing/PricingTiers.vue'),
      meta: { requiresAuth: true, title: 'Pricing Tiers', breadcrumb: 'CRM / Pricing' },
    },
    {
      path: '/crm/pricing/:id',
      name: 'crm-pricing-detail',
      component: () => import('@/views/crm/pricing/PricingTierDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Pricing Tier Details',
        breadcrumb: 'CRM / Pricing / Details',
      },
    },
    {
      path: '/crm/dashboard',
      name: 'crm-dashboard',
      component: () => import('@/views/crm/dashboard/CRMDashboard.vue'),
      meta: { requiresAuth: true, title: 'CRM Dashboard', breadcrumb: 'CRM / Dashboard' },
    },
    {
      path: '/crm/activities',
      name: 'crm-activities',
      component: () => import('@/views/crm/activities/ActivityList.vue'),
      meta: { requiresAuth: true, title: 'Activities', breadcrumb: 'CRM / Activities' },
    },
    // Fleet Routes
    {
      path: '/fleet/dashboard',
      name: 'fleet-dashboard',
      component: () => import('@/views/fleet/dashboard/FleetDashboard.vue'),
      meta: { requiresAuth: true, title: 'Fleet Dashboard', breadcrumb: 'Fleet / Dashboard' },
    },
    {
      path: '/fleet/vehicles',
      name: 'fleet-vehicles',
      component: () => import('@/views/fleet/vehicles/VehicleList.vue'),
      meta: { requiresAuth: true, title: 'Vehicles', breadcrumb: 'Fleet / Vehicles' },
    },
    {
      path: '/fleet/vehicles/:id',
      name: 'fleet-vehicles-detail',
      component: () => import('@/views/fleet/vehicles/VehicleDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Vehicle Details',
        breadcrumb: 'Fleet / Vehicles / Details',
      },
    },
    {
      path: '/fleet/drivers',
      name: 'fleet-drivers',
      component: () => import('@/views/fleet/drivers/DriverList.vue'),
      meta: { requiresAuth: true, title: 'Drivers', breadcrumb: 'Fleet / Drivers' },
    },
    {
      path: '/fleet/drivers/:id',
      name: 'fleet-drivers-detail',
      component: () => import('@/views/fleet/drivers/DriverDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Driver Details',
        breadcrumb: 'Fleet / Drivers / Details',
      },
    },
    {
      path: '/fleet/trips',
      name: 'fleet-trips',
      component: () => import('@/views/fleet/trips/TripList.vue'),
      meta: { requiresAuth: true, title: 'Trips', breadcrumb: 'Fleet / Trips' },
    },
    {
      path: '/fleet/trips/:id',
      name: 'fleet-trips-detail',
      component: () => import('@/views/fleet/trips/TripDetail.vue'),
      meta: { requiresAuth: true, title: 'Trip Details', breadcrumb: 'Fleet / Trips / Details' },
    },
    {
      path: '/fleet/gps',
      name: 'fleet-gps',
      component: () => import('@/views/fleet/gps/GPSTracking.vue'),
      meta: { requiresAuth: true, title: 'GPS Tracking', breadcrumb: 'Fleet / GPS Tracking' },
    },
    {
      path: '/fleet/maintenance',
      name: 'fleet-maintenance',
      component: () => import('@/views/fleet/maintenance/MaintenanceList.vue'),
      meta: { requiresAuth: true, title: 'Maintenance', breadcrumb: 'Fleet / Maintenance' },
    },
    {
      path: '/fleet/maintenance/:id',
      name: 'fleet-maintenance-detail',
      component: () => import('@/views/fleet/maintenance/MaintenanceDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Maintenance Details',
        breadcrumb: 'Fleet / Maintenance / Details',
      },
    },
    {
      path: '/fleet/workshops',
      name: 'fleet-workshops',
      component: () => import('@/views/fleet/workshops/WorkshopList.vue'),
      meta: { requiresAuth: true, title: 'Workshops', breadcrumb: 'Fleet / Workshops' },
    },
    {
      path: '/fleet/workshops/:id',
      name: 'fleet-workshop-detail',
      component: () => import('@/views/fleet/workshops/WorkshopDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Workshop Details',
        breadcrumb: 'Fleet / Workshops / Details',
      },
    },
    {
      path: '/fleet/fuel-log',
      name: 'fleet-fuel-log',
      component: () => import('@/views/fleet/fuel/FuelLog.vue'),
      meta: { requiresAuth: true, title: 'Fuel Log', breadcrumb: 'Fleet / Fuel Log' },
    },
    {
      path: '/fleet/fuel-log/:id',
      name: 'fleet-fuel-log-detail',
      component: () => import('@/views/fleet/fuel/FuelLogDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Fuel Transaction Details',
        breadcrumb: 'Fleet / Fuel Log / Details',
      },
    },
    // Trip Planning Routes
    {
      path: '/fleet/trip-planning',
      name: 'trip-planning-board',
      component: () => import('@/views/fleet/trip-planning/TripPlanningBoard.vue'),
      meta: { requiresAuth: true, title: 'Trip Planning', breadcrumb: 'Fleet / Trip Planning' },
    },
    {
      path: '/fleet/trip-planning/list',
      name: 'trip-planning-list',
      component: () => import('@/views/fleet/trip-planning/TripPlanningList.vue'),
      meta: { requiresAuth: true, title: 'Planned Trips', breadcrumb: 'Fleet / Planned Trips' },
    },
    {
      path: '/fleet/trip-planning/active',
      name: 'trip-planning-active',
      component: () => import('@/views/fleet/trip-planning/ActiveTripsMonitor.vue'),
      meta: { requiresAuth: true, title: 'Active Trips', breadcrumb: 'Fleet / Active Trips' },
    },
    {
      path: '/fleet/trip-planning/trips/:id/manifest',
      name: 'trip-manifest',
      component: () => import('@/views/fleet/trip-planning/TripManifest.vue'),
      meta: { requiresAuth: true, title: 'Trip Manifest', breadcrumb: 'Fleet / Trip Manifest' },
    },
    // Orders Routes
    {
      path: '/orders',
      name: 'orders',
      component: () => import('@/views/orders/OrderList.vue'),
      meta: { requiresAuth: true, title: 'Orders', breadcrumb: 'Orders' },
    },
    {
      path: '/orders/dashboard',
      name: 'orders-dashboard',
      component: () => import('@/views/orders/dashboard/OrdersDashboard.vue'),
      meta: { requiresAuth: true, title: 'Orders Dashboard', breadcrumb: 'Orders / Dashboard' },
    },
    {
      path: '/orders/approvals',
      name: 'orders-approvals',
      component: () => import('@/views/orders/approvals/ApprovalQueue.vue'),
      meta: { requiresAuth: true, title: 'Order Approvals', breadcrumb: 'Orders / Approvals' },
    },
    {
      path: '/orders/bulk-upload',
      name: 'orders-bulk-upload',
      component: () => import('@/views/orders/bulk/BulkOrderUpload.vue'),
      meta: { requiresAuth: true, title: 'Bulk Order Upload', breadcrumb: 'Orders / Bulk Upload' },
    },
    {
      path: '/orders/reports',
      name: 'orders-reports',
      component: () => import('@/views/orders/reports/OrderReports.vue'),
      meta: { requiresAuth: true, title: 'Order Reports', breadcrumb: 'Orders / Reports' },
    },
    {
      path: '/orders/:id',
      name: 'orders-detail',
      component: () => import('@/views/orders/OrderDetail.vue'),
      meta: { requiresAuth: true, title: 'Order Details', breadcrumb: 'Orders / Details' },
    },
    {
      path: '/orders/:id/tracking',
      name: 'orders-tracking',
      component: () => import('@/views/orders/tracking/OrderTracking.vue'),
      meta: { requiresAuth: true, title: 'Order Tracking', breadcrumb: 'Orders / Tracking' },
    },
    // Delivery Routes
    {
      path: '/deliveries',
      name: 'deliveries',
      component: () => import('@/views/orders/deliveries/DeliveryList.vue'),
      meta: { requiresAuth: true, title: 'All Deliveries', breadcrumb: 'Deliveries' },
    },
    {
      path: '/deliveries/active',
      name: 'deliveries-active',
      component: () => import('@/views/orders/deliveries/ActiveDeliveries.vue'),
      meta: { requiresAuth: true, title: 'Active Deliveries', breadcrumb: 'Deliveries / Active' },
    },
    {
      path: '/deliveries/schedule',
      name: 'deliveries-schedule',
      component: () => import('@/views/orders/deliveries/ScheduleDelivery.vue'),
      meta: { requiresAuth: true, title: 'Schedule Delivery', breadcrumb: 'Deliveries / Schedule' },
    },
    {
      path: '/deliveries/:id',
      name: 'delivery-detail',
      component: () => import('@/views/orders/deliveries/DeliveryDetail.vue'),
      meta: { requiresAuth: true, title: 'Delivery Details', breadcrumb: 'Deliveries / Details' },
    },
    // Inventory Routes
    {
      path: '/inventory/tanks',
      name: 'inventory-tanks',
      component: () => import('@/views/inventory/tanks/TankList.vue'),
      meta: { requiresAuth: true, title: 'Tanks', breadcrumb: 'Inventory / Tanks' },
    },
    {
      path: '/inventory/tanks/:id',
      name: 'inventory-tanks-detail',
      component: () => import('@/views/inventory/tanks/TankDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Tank Details',
        breadcrumb: 'Inventory / Tanks / Details',
      },
    },
    {
      path: '/inventory/stock-levels',
      name: 'inventory-stock-levels',
      component: () => import('@/views/inventory/stock-levels/StockLevels.vue'),
      meta: { requiresAuth: true, title: 'Stock Levels', breadcrumb: 'Inventory / Stock Levels' },
    },
    {
      path: '/inventory/adjustments',
      name: 'inventory-adjustments',
      component: () => import('@/views/inventory/adjustments/AdjustmentList.vue'),
      meta: { requiresAuth: true, title: 'Adjustments', breadcrumb: 'Inventory / Adjustments' },
    },
    {
      path: '/inventory/transfers',
      name: 'inventory-transfers',
      component: () => import('@/views/inventory/transfers/TransferList.vue'),
      meta: { requiresAuth: true, title: 'Transfers', breadcrumb: 'Inventory / Transfers' },
    },
    {
      path: '/inventory/transfers/:id',
      name: 'inventory-transfers-detail',
      component: () => import('@/views/inventory/transfers/TransferDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Transfer Details',
        breadcrumb: 'Inventory / Transfers / Details',
      },
    },
    // Supplier Routes
    {
      path: '/suppliers',
      name: 'suppliers',
      component: () => import('@/views/suppliers/SupplierList.vue'),
      meta: { requiresAuth: true, title: 'Suppliers', breadcrumb: 'Suppliers' },
    },
    // Static routes MUST come before dynamic :id route
    {
      path: '/suppliers/purchase-orders',
      name: 'suppliers-purchase-orders',
      component: () => import('@/views/suppliers/PurchaseOrderList.vue'),
      meta: {
        requiresAuth: true,
        title: 'Purchase Orders',
        breadcrumb: 'Suppliers / Purchase Orders',
      },
    },
    {
      path: '/suppliers/purchase-orders/:id',
      name: 'suppliers-purchase-orders-detail',
      component: () => import('@/views/suppliers/PurchaseOrderDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Purchase Order Details',
        breadcrumb: 'Suppliers / PO / Details',
      },
    },
    {
      path: '/suppliers/contacts',
      name: 'suppliers-contacts',
      component: () => import('@/views/suppliers/SupplierContactList.vue'),
      meta: { requiresAuth: true, title: 'Supplier Contacts', breadcrumb: 'Suppliers / Contacts' },
    },
    {
      path: '/suppliers/performance',
      name: 'suppliers-performance',
      component: () => import('@/views/suppliers/SupplierPerformance.vue'),
      meta: {
        requiresAuth: true,
        title: 'Supplier Performance',
        breadcrumb: 'Suppliers / Performance',
      },
    },
    // Dynamic route LAST (after all static routes)
    {
      path: '/suppliers/:id',
      name: 'suppliers-detail',
      component: () => import('@/views/suppliers/SupplierDetail.vue'),
      meta: { requiresAuth: true, title: 'Supplier Details', breadcrumb: 'Suppliers / Details' },
    },
    // Compliance Routes
    {
      path: '/compliance/dashboard',
      name: 'compliance-dashboard',
      component: () => import('@/views/compliance/ComplianceDashboard.vue'),
      meta: {
        requiresAuth: true,
        title: 'Compliance Dashboard',
        breadcrumb: 'Compliance / Dashboard',
      },
    },
    {
      path: '/compliance/permits',
      name: 'compliance-permits',
      component: () => import('@/views/compliance/PermitList.vue'),
      meta: { requiresAuth: true, title: 'Permits & Licenses', breadcrumb: 'Compliance / Permits' },
    },
    {
      path: '/compliance/permits/:id',
      name: 'compliance-permit-detail',
      component: () => import('@/views/compliance/PermitDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Permit Details',
        breadcrumb: 'Compliance / Permits / Details',
      },
    },
    {
      path: '/compliance/inspections',
      name: 'compliance-inspections',
      component: () => import('@/views/compliance/InspectionList.vue'),
      meta: {
        requiresAuth: true,
        title: 'Safety Inspections',
        breadcrumb: 'Compliance / Inspections',
      },
    },
    {
      path: '/compliance/inspections/:id',
      name: 'compliance-inspection-detail',
      component: () => import('@/views/compliance/InspectionDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Inspection Details',
        breadcrumb: 'Compliance / Inspections / Details',
      },
    },
    {
      path: '/compliance/eway-bills',
      name: 'compliance-eway-bills',
      component: () => import('@/views/compliance/EWayBillList.vue'),
      meta: { requiresAuth: true, title: 'E-Way Bills', breadcrumb: 'Compliance / E-Way Bills' },
    },
    {
      path: '/compliance/eway-bills/:id',
      name: 'compliance-eway-bill-detail',
      component: () => import('@/views/compliance/EWayBillDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'E-Way Bill Details',
        breadcrumb: 'Compliance / E-Way Bills / Details',
      },
    },
    // Reporting Routes
    {
      path: '/reports/sales',
      name: 'reports-sales',
      component: () => import('@/views/reports/SalesReports.vue'),
      meta: { requiresAuth: true, title: 'Sales Reports', breadcrumb: 'Reports / Sales' },
    },
    {
      path: '/reports/deliveries',
      name: 'reports-deliveries',
      component: () => import('@/views/reports/DeliveryReports.vue'),
      meta: { requiresAuth: true, title: 'Delivery Reports', breadcrumb: 'Reports / Deliveries' },
    },
    {
      path: '/reports/analytics',
      name: 'reports-analytics',
      component: () => import('@/views/reports/AnalyticsDashboard.vue'),
      meta: { requiresAuth: true, title: 'Analytics Dashboard', breadcrumb: 'Reports / Analytics' },
    },
    {
      path: '/reports/custom',
      name: 'reports-custom',
      component: () => import('@/views/reports/CustomReportBuilder.vue'),
      meta: { requiresAuth: true, title: 'Custom Report Builder', breadcrumb: 'Reports / Custom' },
    },
    // Finance Routes
    {
      path: '/finance/dashboard',
      name: 'finance-dashboard',
      component: () => import('@/views/finance/dashboard/FinanceDashboard.vue'),
      meta: { requiresAuth: true, title: 'Finance Dashboard', breadcrumb: 'Finance / Dashboard' },
    },
    {
      path: '/finance/invoices',
      name: 'finance-invoices',
      component: () => import('@/views/finance/invoices/InvoiceList.vue'),
      meta: { requiresAuth: true, title: 'Invoices', breadcrumb: 'Finance / Invoices' },
    },
    {
      path: '/finance/invoices/pending',
      name: 'finance-invoices-pending',
      component: () => import('@/views/finance/invoices/pending/PendingInvoices.vue'),
      meta: {
        requiresAuth: true,
        title: 'Pending Invoices',
        breadcrumb: 'Finance / Pending Invoices',
      },
    },
    {
      path: '/finance/invoices/:id',
      name: 'finance-invoices-detail',
      component: () => import('@/views/finance/invoices/InvoiceDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Invoice Details',
        breadcrumb: 'Finance / Invoices / Details',
      },
    },
    {
      path: '/finance/invoices/approval-queue',
      name: 'finance-invoices-approval-queue',
      component: () => import('@/views/finance/invoices/InvoiceApprovalQueue.vue'),
      meta: {
        requiresAuth: true,
        title: 'Invoice Approval Queue',
        breadcrumb: 'Finance / Invoices / Approval Queue',
      },
    },
    {
      path: '/finance/invoices/from-orders',
      name: 'finance-invoices-from-orders',
      component: () => import('@/views/finance/invoices/InvoiceFromOrdersWizard.vue'),
      meta: {
        requiresAuth: true,
        title: 'Invoice from Orders',
        breadcrumb: 'Finance / Invoices / From Orders',
      },
    },
    {
      path: '/finance/credit-notes',
      name: 'finance-credit-notes',
      component: () => import('@/views/finance/credit-notes/CreditNoteList.vue'),
      meta: { requiresAuth: true, title: 'Credit Notes', breadcrumb: 'Finance / Credit Notes' },
    },
    {
      path: '/finance/recurring-profiles',
      name: 'finance-recurring-profiles',
      component: () => import('@/views/finance/recurring-profiles/RecurringProfileList.vue'),
      meta: {
        requiresAuth: true,
        title: 'Recurring Invoice Profiles',
        breadcrumb: 'Finance / Recurring Profiles',
      },
    },
    {
      path: '/finance/tax-codes',
      name: 'finance-tax-codes',
      component: () => import('@/views/finance/tax-codes/TaxCodeList.vue'),
      meta: { requiresAuth: true, title: 'Tax Codes', breadcrumb: 'Finance / Tax Codes' },
    },
    {
      path: '/finance/payments',
      name: 'finance-payments',
      component: () => import('@/views/finance/payments/PaymentList.vue'),
      meta: { requiresAuth: true, title: 'Payments', breadcrumb: 'Finance / Payments' },
    },
    {
      path: '/finance/payments/:id',
      name: 'finance-payments-detail',
      component: () => import('@/views/finance/payments/PaymentDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Payment Details',
        breadcrumb: 'Finance / Payments / Details',
      },
    },
    {
      path: '/finance/gst/gstr1',
      name: 'finance-gst-gstr1',
      component: () => import('@/views/finance/gst/gstr1/GSTR1Report.vue'),
      meta: { requiresAuth: true, title: 'GSTR-1', breadcrumb: 'Finance / GST / GSTR-1' },
    },
    {
      path: '/finance/gst/gstr3b',
      name: 'finance-gst-gstr3b',
      component: () => import('@/views/finance/gst/gstr3b/GSTR3BReport.vue'),
      meta: { requiresAuth: true, title: 'GSTR-3B', breadcrumb: 'Finance / GST / GSTR-3B' },
    },
    {
      path: '/finance/gst/einvoice',
      name: 'finance-gst-einvoice',
      component: () => import('@/views/finance/gst/einvoice/EInvoiceList.vue'),
      meta: { requiresAuth: true, title: 'E-Invoice', breadcrumb: 'Finance / GST / E-Invoice' },
    },
    {
      path: '/finance/gst/einvoice/:id',
      name: 'finance-gst-einvoice-detail',
      component: () => import('@/views/finance/gst/einvoice/EInvoiceDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'E-Invoice Details',
        breadcrumb: 'Finance / GST / E-Invoice / Details',
      },
    },
    {
      path: '/finance/bank-accounts',
      name: 'finance-bank-accounts',
      component: () => import('@/views/finance/bank-accounts/BankAccountList.vue'),
      meta: { requiresAuth: true, title: 'Bank Accounts', breadcrumb: 'Finance / Bank Accounts' },
    },
    {
      path: '/finance/bank-accounts/:id',
      name: 'finance-bank-accounts-detail',
      component: () => import('@/views/finance/bank-accounts/BankAccountDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Bank Account Details',
        breadcrumb: 'Finance / Bank Accounts / Details',
      },
    },
    {
      path: '/finance/bank-transactions',
      name: 'finance-bank-transactions',
      component: () => import('@/views/finance/bank-transactions/BankTransactionList.vue'),
      meta: {
        requiresAuth: true,
        title: 'Bank Transactions',
        breadcrumb: 'Finance / Transactions',
      },
    },
    {
      path: '/finance/bank-transactions/:id',
      name: 'finance-bank-transactions-detail',
      component: () => import('@/views/finance/bank-transactions/BankTransactionDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Transaction Details',
        breadcrumb: 'Finance / Transactions / Details',
      },
    },
    {
      path: '/finance/expenses',
      name: 'finance-expenses',
      component: () => import('@/views/finance/expenses/ExpenseList.vue'),
      meta: { requiresAuth: true, title: 'Expenses', breadcrumb: 'Finance / Expenses' },
    },
    {
      path: '/finance/expenses/:id',
      name: 'finance-expenses-detail',
      component: () => import('@/views/finance/expenses/ExpenseDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Expense Details',
        breadcrumb: 'Finance / Expenses / Details',
      },
    },
    {
      path: '/finance/customer-credits',
      name: 'finance-customer-credits',
      component: () => import('@/views/finance/customer-credits/CustomerCreditList.vue'),
      meta: {
        requiresAuth: true,
        title: 'Customer Credits',
        breadcrumb: 'Finance / Customer Credits',
      },
    },
    {
      path: '/finance/customer-credits/:id',
      name: 'finance-customer-credits-detail',
      component: () => import('@/views/finance/customer-credits/CustomerCreditDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Customer Credit Details',
        breadcrumb: 'Finance / Customer Credits / Details',
      },
    },
    // Fixed Assets Routes
    {
      path: '/finance/fixed-assets',
      name: 'finance-fixed-assets',
      component: () => import('@/views/finance/fixed-assets/FixedAssetList.vue'),
      meta: { requiresAuth: true, title: 'Fixed Assets', breadcrumb: 'Finance / Fixed Assets' },
    },
    {
      path: '/finance/fixed-assets/:id',
      name: 'finance-fixed-assets-detail',
      component: () => import('@/views/finance/fixed-assets/FixedAssetDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Fixed Asset Details',
        breadcrumb: 'Finance / Fixed Assets / Details',
      },
    },
    // Loans Routes
    {
      path: '/finance/loans',
      name: 'finance-loans',
      component: () => import('@/views/finance/loans/LoanList.vue'),
      meta: { requiresAuth: true, title: 'Loans', breadcrumb: 'Finance / Loans' },
    },
    {
      path: '/finance/loans/:id',
      name: 'finance-loans-detail',
      component: () => import('@/views/finance/loans/LoanDetail.vue'),
      meta: {
        requiresAuth: true,
        title: 'Loan Details',
        breadcrumb: 'Finance / Loans / Details',
      },
    },
    // Financial Reports Routes
    {
      path: '/finance/reports/balance-sheet',
      name: 'finance-balance-sheet',
      component: () => import('@/views/finance/reports/BalanceSheet.vue'),
      meta: { requiresAuth: true, title: 'Balance Sheet', breadcrumb: 'Finance / Reports / Balance Sheet' },
    },
    {
      path: '/finance/reports/cash-flow',
      name: 'finance-cash-flow',
      component: () => import('@/views/finance/reports/CashFlow.vue'),
      meta: { requiresAuth: true, title: 'Cash Flow Statement', breadcrumb: 'Finance / Reports / Cash Flow' },
    },
    // Contract Routes
    {
      path: '/contracts',
      name: 'contracts',
      component: () => import('@/views/contracts/ContractList.vue'),
      meta: { requiresAuth: true, title: 'Contracts', breadcrumb: 'Contracts' },
    },
    {
      path: '/contracts/pending-approval',
      name: 'contracts-pending-approval',
      component: () => import('@/views/contracts/ContractList.vue'),
      meta: {
        requiresAuth: true,
        title: 'Pending Approval',
        breadcrumb: 'Contracts / Pending Approval',
        defaultFilter: 'pending_approval',
      },
    },
    {
      path: '/contracts/expiring-soon',
      name: 'contracts-expiring-soon',
      component: () => import('@/views/contracts/ContractList.vue'),
      meta: {
        requiresAuth: true,
        title: 'Expiring Soon',
        breadcrumb: 'Contracts / Expiring Soon',
        defaultFilter: 'expiring_soon',
      },
    },
    {
      path: '/contracts/dashboard',
      name: 'contracts-dashboard',
      component: () => import('@/views/contracts/ContractList.vue'),
      meta: {
        requiresAuth: true,
        title: 'Contracts Dashboard',
        breadcrumb: 'Contracts / Dashboard',
      },
    },
    {
      path: '/contracts/clauses',
      name: 'contracts-clauses',
      component: () => import('@/views/contracts/ContractClauseList.vue'),
      meta: {
        requiresAuth: true,
        title: 'Contract Clauses',
        breadcrumb: 'Contracts / Clauses',
      },
    },
    {
      path: '/contracts/:id',
      name: 'contracts-detail',
      component: () => import('@/views/contracts/ContractDetail.vue'),
      meta: { requiresAuth: true, title: 'Contract Details', breadcrumb: 'Contracts / Details' },
    },
    {
      path: '/contracts/:id/deliveries',
      name: 'contracts-deliveries',
      component: () => import('@/views/contracts/ContractDeliveries.vue'),
      meta: {
        requiresAuth: true,
        title: 'Contract Deliveries',
        breadcrumb: 'Contracts / Deliveries',
      },
    },
    // Executive Routes
    {
      path: '/executive/dashboard',
      name: 'executive-dashboard',
      component: () => import('@/views/executive/ExecutiveDashboard.vue'),
      meta: {
        requiresAuth: true,
        title: 'Executive Dashboard',
        breadcrumb: 'Executive / Dashboard',
      },
    },
    // Admin Routes
    {
      path: '/admin/dashboard',
      name: 'admin-dashboard',
      component: () => import('@/views/admin/security/SecurityDashboard.vue'),
      meta: { requiresAuth: true, title: 'Security Dashboard', breadcrumb: 'Admin / Dashboard' },
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      component: () => import('@/views/admin/users/UserList.vue'),
      meta: { requiresAuth: true, title: 'User Management', breadcrumb: 'Admin / Users' },
    },
    {
      path: '/admin/users/import',
      name: 'admin-users-import',
      component: () => import('@/views/admin/users/BulkImport.vue'),
      meta: { requiresAuth: true, title: 'Import Users', breadcrumb: 'Admin / Users / Import' },
    },
    {
      path: '/admin/users/:id',
      name: 'admin-users-detail',
      component: () => import('@/views/admin/users/UserDetail.vue'),
      meta: { requiresAuth: true, title: 'User Details', breadcrumb: 'Admin / Users / Details' },
    },
    {
      path: '/admin/sessions',
      name: 'admin-sessions',
      component: () => import('@/views/admin/security/SessionList.vue'),
      meta: { requiresAuth: true, title: 'Active Sessions', breadcrumb: 'Admin / Sessions' },
    },
    {
      path: '/admin/mfa',
      name: 'admin-mfa',
      component: () => import('@/views/admin/security/MFASettings.vue'),
      meta: { requiresAuth: true, title: 'MFA Settings', breadcrumb: 'Admin / MFA' },
    },
    {
      path: '/admin/audit-log',
      name: 'admin-audit-log',
      component: () => import('@/views/admin/audit/AuditLog.vue'),
      meta: { requiresAuth: true, title: 'Audit Log', breadcrumb: 'Admin / Audit Log' },
    },
    {
      path: '/admin/impersonation-log',
      name: 'admin-impersonation-log',
      component: () => import('@/views/admin/audit/ImpersonationLog.vue'),
      meta: { requiresAuth: true, title: 'Impersonation Log', breadcrumb: 'Admin / Impersonation' },
    },
    {
      path: '/admin/roles',
      name: 'admin-roles',
      component: () => import('@/views/admin/roles/RoleList.vue'),
      meta: { requiresAuth: true, title: 'Role Management', breadcrumb: 'Admin / Roles' },
    },
    {
      path: '/admin/roles/:id',
      name: 'admin-roles-detail',
      component: () => import('@/views/admin/roles/RoleDetail.vue'),
      meta: { requiresAuth: true, title: 'Role Details', breadcrumb: 'Admin / Roles / Details' },
    },
    {
      path: '/admin/permissions',
      name: 'admin-permissions',
      component: () => import('@/views/admin/permissions/PermissionList.vue'),
      meta: { requiresAuth: true, title: 'Permissions', breadcrumb: 'Admin / Permissions' },
    },
    {
      path: '/admin/security-alerts',
      name: 'admin-security-alerts',
      component: () => import('@/views/admin/security/SecurityAlertList.vue'),
      meta: { requiresAuth: true, title: 'Security Alerts', breadcrumb: 'Admin / Security Alerts' },
    },
    // User Profile & Settings Routes
    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/profile/ProfileView.vue'),
      meta: { requiresAuth: true, title: 'Profile Settings', breadcrumb: 'Profile' },
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('@/views/notifications/NotificationsView.vue'),
      meta: { requiresAuth: true, title: 'Notifications', breadcrumb: 'Notifications' },
    },
    {
      path: '/help-support',
      name: 'help-support',
      component: () => import('@/views/help-support/HelpSupportView.vue'),
      meta: { requiresAuth: true, title: 'Help & Support', breadcrumb: 'Help & Support' },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/settings/SettingsView.vue'),
      meta: { requiresAuth: true, title: 'Settings', breadcrumb: 'Settings' },
    },
    // Email Management Routes
    {
      path: '/admin/email-templates',
      name: 'admin-email-templates',
      component: () => import('@/views/admin/email/EmailTemplateList.vue'),
      meta: {
        requiresAuth: true,
        title: 'Email Templates',
        breadcrumb: 'Admin / Email Templates',
      },
    },
    {
      path: '/admin/email-templates/create',
      name: 'admin-email-templates-create',
      component: () => import('@/views/admin/email/EmailTemplateEditor.vue'),
      meta: {
        requiresAuth: true,
        title: 'Create Email Template',
        breadcrumb: 'Admin / Email Templates / Create',
      },
    },
    {
      path: '/admin/email-templates/:id/edit',
      name: 'admin-email-templates-edit',
      component: () => import('@/views/admin/email/EmailTemplateEditor.vue'),
      meta: {
        requiresAuth: true,
        title: 'Edit Email Template',
        breadcrumb: 'Admin / Email Templates / Edit',
      },
    },
    {
      path: '/admin/email-logs',
      name: 'admin-email-logs',
      component: () => import('@/views/admin/email/EmailLogList.vue'),
      meta: {
        requiresAuth: true,
        title: 'Email Logs',
        breadcrumb: 'Admin / Email Logs',
      },
    },
    {
      path: '/admin/company-settings',
      name: 'admin-company-settings',
      component: () => import('@/views/admin/CompanySettings.vue'),
      meta: {
        requiresAuth: true,
        permission: 'company.view',
        title: 'Company Settings',
        breadcrumb: 'Admin / Company Settings',
      },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFound.vue'),
    },
  ],
})

router.beforeEach(authGuard)

router.afterEach(to => {
  const title = (to.meta.title as string) || 'FuelFlow Pro'
  document.title = `${title} - FuelFlow Pro`
})

export default router
